
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CheckCircle2, Info, Tag, X } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";

const formSchema = z.object({
  keyword: z.string().min(2, {
    message: "Keyword must be at least 2 characters."
  }),
  category: z.string().min(1, {
    message: "Please select a category."
  }),
  tags: z.string().min(3, {
    message: "Tags must be at least 3 characters."
  }),
  notes: z.string().optional(),
  domainIds: z.array(z.string()).min(1, { 
    message: "Please select at least one domain box for this KB." 
  }).max(5, {
    message: "You can select up to 5 domain boxes."
  })
});

const KnowledgeBaseDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const kbData = location.state?.kbData || {};
  
  // Fetch iSPAI domains from database
  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ['ispai-domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domains')
        .select('id, name')
        .eq('channel_id', '28570003-dd67-4842-bc21-784df8caaf94')
        .order('name');
      
      if (error) throw error;
      return data;
    }
  });
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      keyword: "",
      category: "",
      tags: "",
      notes: "",
      domainIds: []
    }
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    // Combine the initial KB data with the new values
    const updatedKbData = {
      ...kbData,
      keyword: values.keyword,
      category: values.category,
      tags: values.tags.split(",").map((tag) => tag.trim()),
      notes: values.notes,
      domainIds: values.domainIds
    };
    
    toast({
      title: "Details saved",
      description: "Knowledge base details have been saved successfully."
    });
    
    // Navigate to the next step with the updated data
    navigate("/knowledge-base-documents", { state: { kbData: updatedKbData } });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-serif font-bold text-black mb-4">Knowledge Base Details</h1>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Basic Information</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1 font-medium">
                <Info size={16} />
                <span>Additional Details</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1 text-gray-400">
                <span>Documents</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1 text-gray-400">
                <span>Review & Confirmation</span>
              </div>
            </div>
          </div>
          
          <Card className="border-2 border-black">
            <CardHeader className="bg-slate-100 border-b border-black">
              <CardTitle className="font-serif flex items-center">
                <Tag className="mr-2" size={24} />
                Knowledge Base Details: {kbData.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="keyword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Primary Keyword</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter main keyword for this knowledge base" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Category</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="technology">Technology</SelectItem>
                            <SelectItem value="science">Science</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="health">Health</SelectItem>
                            <SelectItem value="education">Education</SelectItem>
                            <SelectItem value="finance">Finance</SelectItem>
                            <SelectItem value="energy">Energy</SelectItem>
                            <SelectItem value="construction">Construction</SelectItem>
                            <SelectItem value="government">Government</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Tags (comma separated)</FormLabel>
                        <FormControl>
                          <Input placeholder="ai, machine learning, dataset" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="domainIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">
                          Select Domain Boxes (1-5)
                        </FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            if (!field.value.includes(value) && field.value.length < 5) {
                              field.onChange([...field.value, value]);
                            }
                          }}
                          disabled={domainsLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={domainsLoading ? "Loading domains..." : "Select a domain box"} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white z-50">
                            {domains?.map((domain) => (
                              <SelectItem 
                                key={domain.id} 
                                value={domain.id}
                                disabled={field.value.includes(domain.id)}
                              >
                                {domain.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Display selected domains as badges */}
                        {field.value.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {field.value.map((domainId: string) => {
                              const domain = domains?.find(d => d.id === domainId);
                              return (
                                <Badge 
                                  key={domainId} 
                                  variant="outline" 
                                  className="border-black flex items-center gap-1"
                                >
                                  {domain?.name}
                                  <X 
                                    className="h-3 w-3 cursor-pointer" 
                                    onClick={() => {
                                      field.onChange(field.value.filter((id: string) => id !== domainId));
                                    }}
                                  />
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        
                        <p className="text-sm text-muted-foreground mt-1">
                          {field.value.length} of 5 domain boxes selected
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Additional Notes</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Any additional information about this knowledge base" 
                            className="min-h-32" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-between pt-4">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => navigate("/creators")}
                    >
                      Back
                    </Button>
                    <Button type="submit" className="px-8">
                      Continue
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default KnowledgeBaseDetails;
